import toast from 'react-hot-toast';
import { t } from 'i18next';

import { notification } from '@/utils/notification';
import type { InAppNotification } from '@/types/inAppNotification';

import { resolveInAppNotificationUrl } from './resolveInAppNotificationUrl';

const MAX_INDIVIDUAL_TOASTS = 3;
const MAX_REMEMBERED_IDS = 200;

const announcedIds: number[] = [];
const announcedIdSet = new Set<number>();

const remember = (id: number) => {
  announcedIds.push(id);
  announcedIdSet.add(id);

  while (announcedIds.length > MAX_REMEMBERED_IDS) {
    const evicted = announcedIds.shift();

    if (evicted !== undefined) {
      announcedIdSet.delete(evicted);
    }
  }
};

export const resetAnnouncedInAppNotifications = () => {
  announcedIds.length = 0;
  announcedIdSet.clear();
};

/**
 * Toasts notifications that arrived live.
 *
 * Only ever called from the socket's event handler, never from a cache merge.
 */
export const announceInAppNotifications = (
  notifications: InAppNotification[],
  onView: (notification: InAppNotification) => void,
) => {
  const unannounced = notifications.filter(({ id }) => !announcedIdSet.has(id));

  if (unannounced.length === 0) {
    return;
  }

  unannounced.forEach(({ id }) => remember(id));

  if (unannounced.length > MAX_INDIVIDUAL_TOASTS) {
    notification.info({
      body: t('inAppNotification:toast.multiple', { count: unannounced.length }),
    });

    return;
  }

  unannounced.forEach((item) => {
    const toastId: string = notification.info({
      title: item.title ?? t('inAppNotification:toast.defaultTitle'),
      action: resolveInAppNotificationUrl(item.url, item.referenceType, item.referenceId).kind !== 'none'
        ? {
          size: 'small',
          variant: 'light',
          text: t('inAppNotification:toast.view'),
          onClick: () => {
            toast.dismiss(toastId);
            onView(item);
          },
        }
        : undefined,
    });
  });
};
